import { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";
import { GLOBE_RADIUS, latLonToVector3, tangentBasis, fractureFactor } from "../lib/seismic";
import { useSim } from "../lib/store";

export function FractureLines({ event }) {
  const matRef = useRef();

  const geometry = useMemo(() => {
    if (!event) return null;
    const f = fractureFactor(event.duration);
    const count = 4 + Math.round(f * 46);
    const maxAngle = 0.05 + f * 0.55;
    const epi = latLonToVector3(event.lat, event.lon, 1).normalize();
    const { t1, t2 } = tangentBasis(epi);
    const positions = [];
    const steps = 10;
    for (let c = 0; c < count; c++) {
      const baseAz = Math.random() * Math.PI * 2;
      const bend = (Math.random() - 0.5) * 1.4;
      const len = maxAngle * (0.5 + Math.random() * 0.5);
      let prev = null;
      for (let k = 0; k <= steps; k++) {
        const t = k / steps;
        const dist = t * len;
        const az = baseAz + bend * t + Math.sin(t * 6 + c) * 0.08;
        const dir = t1.clone().multiplyScalar(Math.cos(az)).add(t2.clone().multiplyScalar(Math.sin(az)));
        const sp = epi.clone().multiplyScalar(Math.cos(dist)).add(dir.multiplyScalar(Math.sin(dist))).normalize();
        const p = sp.multiplyScalar(GLOBE_RADIUS + 0.004);
        if (prev) positions.push(prev.x, prev.y, prev.z, p.x, p.y, p.z);
        prev = p;
      }
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    return geom;
  }, [event]);

  useFrame(() => {
    if (!matRef.current || !event) return;
    const st = useSim.getState();
    const elapsed = (performance.now() - st.simStart) / 1000;
    const running = st.simulating && elapsed <= event.duration + 0.5;
    const p = running ? Math.min(elapsed / event.duration, 1) : 0;
    const target = running ? 0.35 + p * 0.65 : 0;
    matRef.current.opacity += (target - matRef.current.opacity) * 0.1;
  });

  if (!geometry) return null;

  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial ref={matRef} color="#ff3322" transparent opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
    </lineSegments>
  );
}
